/**
 * use-accounts — reactive view over the web multi-account registry + the
 * switch/add/remove orchestration used by the account switcher UI.
 *
 * Switching an account is data-preserving: we set the active pointer, re-prime
 * the session actor for the new identity and let the PowerSync provider swap to
 * that user's DB instance (it reacts to the active user id). We NEVER call
 * `disconnectAndClear` on a switch — that would destroy the data. Only an
 * explicit "Sign out" / "Remove account" wipes a database.
 */

import { createSignal, onCleanup, onMount } from "solid-js";
import { useLocation, useNavigate } from "@tanstack/solid-router";
import {
  type AccountsState,
  emptyAccountsState,
  getActiveAccount,
  type StoredAccount,
} from "@famidex/shared";
import { ACCOUNTS_CHANGED_EVENT, accountsStore } from "@/lib/accounts";
import {
  indexOfUserId,
  parseActiveIndexFromPath,
  readActiveIndexFromUrl,
  rememberLastActiveIndex,
  resolveTabAccount,
} from "@/lib/active-account";
import { primeSessionForAccount } from "@/lib/session-machine";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { queryClient } from "@/lib/query-client";

export function useAccounts() {
  const navigate = useNavigate();
  const location = useLocation();
  const { destroyAccountData } = usePowerSyncContext();
  const [state, setState] = createSignal<AccountsState>(emptyAccountsState());

  // localStorage is the source of truth; reload on mount and on cross-tab
  // writes so a switch/add/remove in another tab is reflected here too.
  const reload = () => {
    void (async () => {
      setState(await accountsStore.load());
    })();
  };

  onMount(() => {
    reload();
    // Cross-tab: the `storage` event fires in OTHER tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key === "famidex_accounts" || e.key === null) {
        reload();
      }
    };
    // Same-tab: `storage` never fires for our own writes, so a sign-out/remove in
    // THIS tab must be picked up via the registry's custom change event — else the
    // signed-out account lingers in the switcher until the next interaction.
    const onLocalChange = () => {
      reload();
    };
    globalThis.addEventListener("storage", onStorage);
    globalThis.addEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    onCleanup(() => {
      globalThis.removeEventListener("storage", onStorage);
      globalThis.removeEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    });
  });

  const accounts = () => state().accounts;

  // Per-tab active account: which account is active in THIS tab is encoded in the
  // URL `/u/<n>` index, NOT in the registry's global `activeUserId` (that is only
  // a "last-used" default shared across tabs). The switcher MUST highlight — and
  // its switch-guard MUST compare against — the tab's URL-derived account, or two
  // tabs would both show the registry's last-active account as "current" and the
  // guard `userId === activeUserId()` would wrongly short-circuit a real switch.
  // Reading `location().pathname` makes this reactive to in-tab navigation.
  const active = (): StoredAccount | null => {
    const urlIndex = parseActiveIndexFromPath(location().pathname);
    return resolveTabAccount(state(), urlIndex);
  };
  const activeUserId = () => active()?.userId ?? null;

  /**
   * Switches THIS TAB to a known account WITHOUT clearing any data, by
   * NAVIGATING to that account's `/u/<index>` prefix. Only this tab changes;
   * other tabs keep their own `/u/<n>`. We still keep the registry's
   * `activeUserId` as the "last-used" default for fresh tabs / redirects, but it
   * no longer drives which account is active in any given tab — the URL does.
   */
  const switchTo = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    const index = indexOfUserId(next, userId);
    if (index === null) {
      return;
    }
    rememberLastActiveIndex(index);
    // Re-prime the session machine for the target identity (the machine is not
    // URL-reactive) and drop the previous account's cached server data so
    // queries refetch under the new identity (the local DB is per-user).
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    // Navigate to the target account's route using the TYPED param form
    // (`to: "/u/$u"` + `params`). A flat string `to: "/u/<n>/"` is NOT navigated
    // when we're already inside the `/u/$u` tree (TanStack resolves it against the
    // current match and treats the same flat path as a no-op), so switching from
    // `/u/1/inbox` to `/u/0/` silently did nothing. The param form always commits.
    await navigate({ to: "/u/$u", params: { u: index } });
  };

  /**
   * Opens the login screen in "add" mode to add another account WITHOUT
   * signing the current one out (Gmail / Microsoft To Do style). The `mode=add`
   * flag tells the auth flow to skip the pre-sign-out so the existing account
   * stays primed in the registry.
   */
  const addAccount = async () => {
    await navigate({ to: "/login", search: { redirect: "/", mode: "add" } });
  };

  /**
   * Activates the explicit guest account (visitor / local-only mode) in THIS
   * tab by navigating to its `/u/<index>`.
   */
  const continueAsGuest = async () => {
    const next = await accountsStore.ensureGuestActive();
    setState(next);
    const guest = getActiveAccount(next);
    const index = guest ? indexOfUserId(next, guest.userId) : null;
    if (index === null) {
      return;
    }
    rememberLastActiveIndex(index);
    queryClient.clear();
    primeSessionForAccount(guest);
    await navigate({ to: "/u/$u", params: { u: index } });
  };

  /**
   * Removes an account from the registry and destroys its local database. If it
   * was THIS TAB's active account, we fall back to a remaining account and
   * navigate this tab to its `/u/<index>`. Indices shift on removal, so we
   * recompute from the post-removal state.
   */
  const removeAccount = async (account: StoredAccount) => {
    const wasActive = readActiveIndexFromUrl() === indexOfUserId(state(), account.userId);
    const next = await accountsStore.remove(account.userId);
    setState(next);
    await destroyAccountData(account.userId);
    if (wasActive) {
      const fallback = getActiveAccount(next);
      queryClient.clear();
      primeSessionForAccount(fallback);
      if (fallback) {
        const index = indexOfUserId(next, fallback.userId) ?? 0;
        rememberLastActiveIndex(index);
        await navigate({ to: "/u/$u", params: { u: index } });
      } else {
        await navigate({ to: "/login" });
      }
    }
  };

  return {
    accounts,
    activeUserId,
    active,
    switchTo,
    addAccount,
    continueAsGuest,
    removeAccount,
    reload,
  };
}
